import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { File as FileIcon, Film, Image as ImageIcon, Music, RefreshCw, X } from "lucide-react";
import { Button, DialogCancel, Field, Icon, Modal, Picker, PlatformIcon, Spinner } from "@cntrl/ui";
import { useBandContext } from "../../app/bandContext.js";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { useRelease } from "../../app/hooks/useRelease.js";
import { releaseKey } from "../../app/hooks/useReleases.js";
import {
  isImportInFlight,
  useCreateReleaseImport,
  useReleaseImports,
  useRetryReleaseImportItem,
  type ImportSource,
  type ReleaseImportItemView,
  type ReleaseImportView,
} from "../../app/hooks/useReleaseImports.js";
import { useDropboxChooser, type ChooserFile } from "./dropboxChooser.js";
import { VARIANT_KINDS } from "./releaseVariantKinds.js";
import {
  describeSize,
  IMPORT_ROLE_LABELS,
  isSingleRole,
  mediaKindOf,
  needsSong,
  rolesFor,
  suggestRoles,
  type ImportRole,
  type ImportRowDraft,
} from "./importRoles.js";
import styles from "./ImportFilesModal.module.scss";

const PHOTO_ROLES = ["Profile", "Press", "Live", "Promo", "Behind the scenes", "Other"];

type Step = "pick" | "roles" | "progress";

/** A file picked in the vendor's picker, with the kind we read off its name. */
interface PickedFile {
  id: string;
  name: string;
  link: string;
  sizeBytes: number;
  mediaKind: string;
}

/**
 * Import files (ART-369) — bring a release's files over from Dropbox, as part of moving to Artist CNTRL. Three
 * steps: pick files in Dropbox's own picker, give each one a role in THIS release, then watch them land.
 *
 * Nothing is copied by the browser and no Dropbox account is connected to us. The picker hands back a download link
 * for each file, good for four hours; the server queues one job per file and the worker copies it from that link
 * into our media, so a 1 GB master never passes through the person's laptop, and the dialog can be closed while it
 * runs — reopening it picks the running import back up from the release's import list.
 */
export function ImportFilesModal({ releaseId, onClose }: { releaseId: string; onClose: () => void }) {
  const imports = useReleaseImports(releaseId);
  const release = useRelease(releaseId);
  const latest = imports.data?.imports?.[0];

  const [step, setStep] = useState<Step | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [picked, setPicked] = useState<PickedFile[]>([]);
  const [running, setRunning] = useState<string | null>(null);

  // Open on the running import if there is one; otherwise on the picker.
  useEffect(() => {
    if (step !== null || !imports.data) return;
    if (latest?.items?.some(isImportInFlight)) {
      setRunning(latest.importId!);
      setStep("progress");
    } else {
      setStep("pick");
    }
  }, [step, imports.data, latest]);

  if (step === null || imports.isLoading) {
    return (
      <Modal title="Import files" onClose={onClose} size="xl">
        <div className={styles.loading} role="status" aria-live="polite">
          <Spinner size={20} label="Loading…" />
        </div>
      </Modal>
    );
  }

  const limits = imports.data?.limits;

  if (step === "pick") {
    return (
      <PickStep
        sources={imports.data?.sources ?? []}
        sizeLimit={limits ? Math.max(limits.maxUploadBytes ?? 0, limits.maxAudioUploadBytes ?? 0) || undefined : undefined}
        onClose={onClose}
        onPicked={(source, files) => {
          setProvider(source);
          setPicked(files);
          setStep("roles");
        }}
      />
    );
  }

  if (step === "roles" && provider) {
    const sourceName = imports.data?.sources?.find((s) => s.provider === provider)?.displayName ?? "storage";
    return (
      <RolesStep
        title={`Import from ${sourceName}`}
        releaseId={releaseId}
        provider={provider}
        files={picked}
        songs={(release.data?.data?.tracks ?? []).map((t) => ({ trackId: t.trackId!, title: t.title ?? "" }))}
        hasCover={(release.data?.data?.assets?.items ?? []).some((a) => a.kind === "cover" && a.mediaId)}
        hasCanvas={(release.data?.data?.assets?.items ?? []).some((a) => a.kind === "canvas" && a.mediaId)}
        hasLyrics={(release.data?.data?.assets?.items ?? []).some((a) => a.kind === "lyrics" && a.textContent)}
        mastered={new Set((release.data?.data?.tracks ?? []).filter((t) => t.masterMediaId).map((t) => t.trackId!))}
        limits={limits}
        onBack={() => setStep("pick")}
        onClose={onClose}
        onQueued={(imp) => {
          setRunning(imp.importId!);
          setPicked([]);
          setStep("progress");
        }}
      />
    );
  }

  const shown = imports.data?.imports?.find((i) => i.importId === running) ?? latest;
  return <ProgressStep releaseId={releaseId} view={shown} onClose={onClose} onImportMore={() => setStep("pick")} />;
}

// ---- step 1: pick --------------------------------------------------------------------------------------

function PickStep({
  sources,
  sizeLimit,
  onPicked,
  onClose,
}: {
  sources: ImportSource[];
  sizeLimit: number | undefined;
  onPicked: (provider: string, files: PickedFile[]) => void;
  onClose: () => void;
}) {
  const dropbox = sources.find((s) => s.provider === "dropbox");
  // Loaded as the dialog opens: the picker is a popup, so it has to open inside the click itself.
  const chooser = useDropboxChooser(dropbox?.available ? dropbox.pickerKey : null);

  const pickFromDropbox = () =>
    chooser.choose(sizeLimit, (files: ChooserFile[]) => {
      if (files.length === 0) return;
      onPicked(
        "dropbox",
        files.map((f) => ({ id: f.id, name: f.name, link: f.link, sizeBytes: f.bytes, mediaKind: mediaKindOf(f.name) })),
      );
    });

  return (
    <Modal title="Import files" onClose={onClose} size="md" footer={<DialogCancel />}>
      <ul className={styles.sources}>
        {sources.map((s) => {
          const isDropbox = s.provider === "dropbox";
          const meta = !s.available
            ? "Setup pending"
            : isDropbox && chooser.state === "unsupported"
              ? "This browser can't open Dropbox's picker."
              : isDropbox && chooser.state === "failed"
                ? "Dropbox's picker didn't load. Check your connection and open this again."
                : null;
          return (
            <li key={s.provider} className={styles.source}>
              <PlatformIcon platform={s.provider} size={24} />
              <span className={styles.sourceText}>
                <span className={styles.sourceName}>{s.displayName}</span>
                {meta && <span className={styles.sourceMeta}>{meta}</span>}
              </span>
              {s.available && isDropbox && (
                <Button
                  size="sm"
                  variant="accent"
                  loading={chooser.state === "loading"}
                  disabled={chooser.state !== "ready"}
                  onClick={pickFromDropbox}
                >
                  Choose files
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

function kindIcon(kind: string | null | undefined) {
  if (kind === "image") return ImageIcon;
  if (kind === "audio") return Music;
  if (kind === "video") return Film;
  return FileIcon;
}

// ---- step 3: roles -------------------------------------------------------------------------------------

function RolesStep({
  title,
  releaseId,
  provider,
  files,
  songs,
  hasCover,
  hasCanvas,
  hasLyrics,
  mastered,
  limits,
  onBack,
  onClose,
  onQueued,
}: {
  title: string;
  releaseId: string;
  provider: string;
  files: PickedFile[];
  songs: { trackId: string; title: string }[];
  hasCover: boolean;
  hasCanvas: boolean;
  hasLyrics: boolean;
  mastered: Set<string>;
  limits?: { maxUploadBytes?: number; maxAudioUploadBytes?: number; maxPhotos?: number; maxItems?: number };
  onBack: () => void;
  onClose: () => void;
  onQueued: (view: ReleaseImportView) => void;
}) {
  const create = useCreateReleaseImport(releaseId);
  const [rows, setRows] = useState<PickedFile[]>(files);
  const [drafts, setDrafts] = useState<Map<string, ImportRowDraft>>(() =>
    suggestRoles(files, songs),
  );
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const update = (id: string, patch: Partial<ImportRowDraft>) => {
    const next = new Map(drafts);
    next.set(id, { ...next.get(id)!, ...patch });
    setDrafts(next);
    setServerErrors({});
  };

  // What the client can already see is wrong, checked with the server's own limits and rules.
  const problems = new Map<string, string>();
  const taken = new Map<string, string>();
  for (const f of rows) {
    const d = drafts.get(f.id);
    if (!d) continue;
    const cap = f.mediaKind === "audio" ? limits?.maxAudioUploadBytes : limits?.maxUploadBytes;
    if (cap && f.sizeBytes > cap) problems.set(f.id, `Too large. The limit is ${describeSize(cap)}.`);
    else if (needsSong(d.role) && !d.trackId) problems.set(f.id, "Pick which song this is.");
    else if (d.role === "version" && !d.label) problems.set(f.id, "Pick which version this is.");
    const key = d.role === "master" ? `master:${d.trackId}` : isSingleRole(d.role) ? d.role : null;
    if (key) {
      if (taken.has(key)) problems.set(f.id, `Only one file can be the ${IMPORT_ROLE_LABELS[d.role].toLowerCase()}.`);
      else taken.set(key, f.id);
    }
  }
  const photoCount = rows.filter((f) => drafts.get(f.id)?.role === "photo").length;
  const tooManyPhotos = limits?.maxPhotos != null && photoCount > limits.maxPhotos;
  const tooMany = limits?.maxItems != null && rows.length > limits.maxItems;

  const submit = () => {
    create.mutate(
      {
        provider,
        items: rows.map((f) => {
          const d = drafts.get(f.id)!;
          return {
            sourceId: f.id,
            link: f.link,
            name: f.name,
            sizeBytes: f.sizeBytes,
            role: d.role,
            trackId: needsSong(d.role) ? (d.trackId ?? null) : null,
            label: d.role === "file" || d.role === "version" ? (d.label ?? null) : null,
            photoRole: d.role === "photo" ? (d.photoRole ?? null) : null,
          };
        }),
      },
      {
        onSuccess: onQueued,
        onError: (err) => setServerErrors(rowErrors(err, rows)),
      },
    );
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onBack} disabled={create.isPending}>
        Back
      </Button>
      <Button
        onClick={submit}
        loading={create.isPending}
        disabled={rows.length === 0 || problems.size > 0 || tooManyPhotos || tooMany}
      >
        Import {rows.length} file{rows.length === 1 ? "" : "s"}
      </Button>
    </>
  );

  const generalError = create.isError && Object.keys(serverErrors).length === 0 ? errorMessage(create.error, "Couldn't start the import.") : null;

  return (
    <Modal title={title} onClose={onClose} size="xl" footer={footer} dirty>
      {tooManyPhotos && (
        <p className={styles.warn} role="alert">
          A release holds at most {limits?.maxPhotos} photos. Make some of these files instead.
        </p>
      )}
      {tooMany && (
        <p className={styles.warn} role="alert">
          Import at most {limits?.maxItems} files at a time.
        </p>
      )}
      {generalError && (
        <p className={styles.warn} role="alert">
          {generalError}
        </p>
      )}
      <ul className={styles.roles}>
        {rows.map((f) => {
          const d = drafts.get(f.id)!;
          const replaces =
            (d.role === "cover" && hasCover) ||
            (d.role === "canvas" && hasCanvas) ||
            (d.role === "lyrics" && hasLyrics) ||
            (d.role === "master" && d.trackId != null && mastered.has(d.trackId));
          const problem = problems.get(f.id) ?? serverErrors[f.id];
          return (
            <li key={f.id} className={styles.roleRow} data-problem={problem ? "" : undefined}>
              <span className={styles.roleFile}>
                <Icon icon={kindIcon(f.mediaKind)} size={16} />
                <span className={styles.roleFileText}>
                  <span className={styles.entryName}>{f.name}</span>
                  <span className={styles.entryMeta}>
                    {describeSize(f.sizeBytes)}
                    {replaces && !problem ? ` · Replaces the current ${IMPORT_ROLE_LABELS[d.role].toLowerCase()}` : ""}
                  </span>
                  {problem && <span className={styles.rowError}>{problem}</span>}
                </span>
              </span>
              <span className={styles.roleControls}>
                <Picker
                  label={`What ${f.name} is`}
                  hideLabel
                  value={d.role}
                  options={rolesFor(f.mediaKind, f.name).map((r) => ({ value: r, label: IMPORT_ROLE_LABELS[r] }))}
                  onChange={(v) => {
                    const role = v as ImportRole;
                    update(f.id, {
                      role,
                      label: role === "file" ? (d.label && d.role === "file" ? d.label : f.name) : role === "version" ? d.label : undefined,
                      photoRole: role === "photo" ? (d.photoRole ?? "Press") : undefined,
                    });
                  }}
                />
                <SecondControl file={f} draft={d} songs={songs} onChange={(patch) => update(f.id, patch)} />
              </span>
              <button
                type="button"
                className={styles.remove}
                aria-label={`Don't import ${f.name}`}
                title="Don't import"
                onClick={() => setRows(rows.filter((r) => r.id !== f.id))}
              >
                <Icon icon={X} size={16} />
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

/** The role's own detail: which song, which version, which photo role, or a file's label. */
function SecondControl({
  file,
  draft,
  songs,
  onChange,
}: {
  file: PickedFile;
  draft: ImportRowDraft;
  songs: { trackId: string; title: string }[];
  onChange: (patch: Partial<ImportRowDraft>) => void;
}) {
  if (needsSong(draft.role)) {
    if (songs.length === 0) return <span className={styles.entryMeta}>Add the song on the Assets tab first.</span>;
    return (
      <span className={styles.pair}>
        <Picker
          label={`Song for ${file.name}`}
          hideLabel
          value={draft.trackId ?? ""}
          options={[{ value: "", label: "Pick a song", disabled: true }, ...songs.map((s) => ({ value: s.trackId, label: s.title }))]}
          onChange={(v) => onChange({ trackId: v || undefined })}
        />
        {draft.role === "version" && (
          <Picker
            label={`Version of ${file.name}`}
            hideLabel
            value={draft.label ?? ""}
            options={[{ value: "", label: "Pick a version", disabled: true }, ...VARIANT_KINDS.map((k) => ({ value: k.label, label: k.label }))]}
            onChange={(v) => onChange({ label: v || undefined })}
          />
        )}
      </span>
    );
  }
  if (draft.role === "photo") {
    return (
      <Picker
        label={`Photo role for ${file.name}`}
        hideLabel
        value={draft.photoRole ?? "Press"}
        options={PHOTO_ROLES.map((r) => ({ value: r, label: r }))}
        onChange={(v) => onChange({ photoRole: v })}
      />
    );
  }
  if (draft.role === "file") {
    return (
      <Field
        label={`Label for ${file.name}`}
        hideLabel
        value={draft.label ?? ""}
        maxLength={80}
        onChange={(e) => onChange({ label: e.target.value })}
      />
    );
  }
  return null;
}

/** The server's per-row refusals (`items[n]`), keyed back to the row's file id. */
function rowErrors(err: unknown, rows: PickedFile[]): Record<string, string> {
  if (!(err instanceof ApiError) || !err.problem || typeof err.problem !== "object") return {};
  const errors = (err.problem as { errors?: Record<string, string[]> }).errors ?? {};
  const out: Record<string, string> = {};
  for (const [key, messages] of Object.entries(errors)) {
    const m = /^items\[(\d+)\]$/.exec(key);
    const row = m ? rows[Number(m[1])] : undefined;
    if (row && messages[0]) out[row.id] = messages[0];
  }
  return out;
}

// ---- step 4: progress ----------------------------------------------------------------------------------

function ProgressStep({
  releaseId,
  view,
  onClose,
  onImportMore,
}: {
  releaseId: string;
  view: ReleaseImportView | undefined;
  onClose: () => void;
  onImportMore: () => void;
}) {
  const retry = useRetryReleaseImportItem(releaseId);
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const items = view?.items ?? [];
  const placed = items.filter((i) => i.status === "placed").length;
  const failed = items.filter((i) => i.status === "failed").length;
  const inFlight = items.filter(isImportInFlight).length;

  // Repaint the Assets tab as files land, not only when the whole import is done.
  const lastPlaced = useRef(placed);
  useEffect(() => {
    if (placed !== lastPlaced.current) {
      lastPlaced.current = placed;
      void qc.invalidateQueries({ queryKey: releaseKey(bandId, releaseId) });
    }
  }, [placed, qc, bandId, releaseId]);

  const footer = (
    <>
      <Button variant="ghost" onClick={onImportMore}>
        Import more
      </Button>
      <Button onClick={onClose}>{inFlight > 0 ? "Close" : "Done"}</Button>
    </>
  );

  return (
    <Modal title="Import files" onClose={onClose} size="lg" footer={footer}>
      <p className={styles.intro} aria-live="polite">
        {inFlight > 0
          ? `Copying ${inFlight} of ${items.length}. (You can close this window.)`
          : failed > 0
            ? `${placed} of ${items.length} imported. ${failed} didn't make it.`
            : `${items.length} file${items.length === 1 ? "" : "s"} imported.`}
      </p>
      <ul className={styles.progress}>
        {items.map((i) => (
          <ProgressRow
            key={i.itemId}
            item={i}
            retrying={retry.isPending && retry.variables?.itemId === i.itemId}
            onRetry={() => retry.mutate({ importId: view!.importId!, itemId: i.itemId! })}
          />
        ))}
      </ul>
    </Modal>
  );
}

function ProgressRow({ item, retrying, onRetry }: { item: ReleaseImportItemView; retrying: boolean; onRetry: () => void }) {
  const role = IMPORT_ROLE_LABELS[item.role as ImportRole] ?? "File";
  return (
    <li className={styles.progressRow} data-status={item.status}>
      <span className={styles.roleFileText}>
        <span className={styles.entryName}>{item.name}</span>
        <span className={styles.entryMeta}>
          {role}
          {item.label && item.role !== "file" ? ` · ${item.label}` : ""}
        </span>
        {item.status === "failed" && item.error && <span className={styles.rowError}>{item.error}</span>}
        {item.needsRepick && (
          // The picker's link lasts four hours; past that only a fresh pick can fetch the file again.
          <span className={styles.rowError}>The link from Dropbox has expired. Use Import more to pick this file again.</span>
        )}
      </span>
      <span className={styles.status}>
        {item.status === "placed" ? (
          "Done"
        ) : item.canRetry ? (
          // A failed row, or one stuck copying long after any copy finishes (its worker died): the server says which.
          <Button size="sm" variant="ghost" loading={retrying} onClick={onRetry}>
            <Icon icon={RefreshCw} size={16} /> Retry
          </Button>
        ) : item.status === "failed" ? (
          "Failed"
        ) : (
          <>
            <Spinner size={16} label={item.status === "copying" ? "Copying" : "Waiting"} />
            {/* The spinner carries the accessible name; the word is for the eye. */}
            <span aria-hidden="true">{item.status === "copying" ? "Copying" : "Waiting"}</span>
          </>
        )}
      </span>
    </li>
  );
}
